// Inner letters are preserved, so "HTTPRequest" stays "HTTPRequest".
export function titleCaseSnake(s: string): string {
    return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// With useRecency (default), recent dates show as relative time (e.g. "5m ago").
export function formatDateTime(dateStr: string, useRecency: boolean = true): string {
    const d = new Date(dateStr);
    if (useRecency) {
        const diffMs = Date.now() - d.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
    }
    return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

export interface ParagraphChange {
    text: string;
    /** Whether the other side lacks this paragraph. */
    changed: boolean;
}

// Two markdown texts split at blank lines, each paragraph marked where the other text
// lacks it, by longest common subsequence.
export function diffParagraphs(before: string, after: string): { before: ParagraphChange[]; after: ParagraphChange[] } {
    const split = (s: string) => s.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    const a = split(before), b = split(after);
    const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--)
        for (let j = b.length - 1; j >= 0; j--)
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    const keptA = new Set<number>(), keptB = new Set<number>();
    for (let i = 0, j = 0; i < a.length && j < b.length;) {
        if (a[i] === b[j]) { keptA.add(i++); keptB.add(j++); }
        else if (lcs[i + 1][j] >= lcs[i][j + 1]) i++;
        else j++;
    }
    return {
        before: a.map((text, i) => ({ text, changed: !keptA.has(i) })),
        after: b.map((text, j) => ({ text, changed: !keptB.has(j) })),
    };
}
